import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client'
import { relayStylePagination } from '@apollo/client/utilities'
import fetch from 'isomorphic-fetch'
import React, { useContext, useEffect } from 'react'
import { BehaviorSubject, forkJoin, from, of, switchMap, take, tap } from 'rxjs'

import commaPossibleTypes from '../graphql/search/possible-types'
import { TokenContext } from '../layouts/context'
import { latestAuthStateWithAccessToken$ } from '../lib/auth/state'
import { isValidDispensaryPath } from '../lib/dispensaries'
import {
  ApolloClients,
  ApolloClientsContext,
  latestApolloClients$,
  setApolloClients,
} from '../lib/graphql/apollo'
import { createExcludeLiveRatesLink } from '../lib/graphql/exclude-live-rates-link'
import { FetchError, logAndCaptureException } from '../utils/errorTools'
import { fromApolloObservable, toApolloObservable } from '../utils/rx/apollo-observable'

/**
 * Custom fetch function to handle errors from the server. Apollo Router will return a non-JSON
 * error message if it catches the timeout. Check the response status and throw an error before
 * Apollo tries to parse the response as JSON.
 */
const customFetch: typeof fetch = async (uri, options) => {
  const response = await fetch(uri, options)
  if (response.status >= 400) {
    const statusText = response.statusText ? ` ${response.statusText}` : ''
    const responseBody = await response.text()
    const responseBodySummary =
      responseBody.length > 100 ? `${responseBody.slice(0, 100)}...` : responseBody
    return Promise.reject(
      new FetchError(
        `${response.status}${statusText}${responseBodySummary ? ': ' : ''}${responseBodySummary}`,
        response,
        responseBody,
      ),
    )
  }
  return response
}

const commaLink = new HttpLink({
  uri: process.env.GATSBY_SEARCH_URL,
  fetch: customFetch,
})

const magentoLink = new HttpLink({
  uri: process.env.GATSBY_MAGENTO_URL,
  fetch,
})

const authMiddleware = new ApolloLink((operation, forward) => {
  const { token, useLatestTokenIfLoggedIn } = operation.getContext()

  // Much of existing code uses a token context and then sets it in the operation context. We've
  // added a new context option to automatically use the latest token if the user is logged in.
  // This might reduce the need for some boilerplate.
  if (useLatestTokenIfLoggedIn) {
    return toApolloObservable(
      latestAuthStateWithAccessToken$.pipe(
        take(1),
        tap((authState) => {
          if (authState.isAuthenticated) {
            operation.setContext({
              headers: {
                Authorization: `Bearer ${authState.accessToken}`,
              },
            })
          }
        }),
        switchMap(() => fromApolloObservable(forward(operation))),
      ),
    )
  }

  // Set token explicitly from context
  if (token) {
    operation.setContext({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  }
  return forward(operation)
})

/**
 * Set `x-dfh-site-context` header to reflect whether requests originate from a dispensary. This
 * enables varied behavior (e.g. price level) for unauthenticated requests.
 */
const siteContextMiddleware = new ApolloLink((operation, forward) => {
  const isDispensary =
    typeof window !== 'undefined' && isValidDispensaryPath(window.location.pathname)
  const headers: unknown = operation.getContext().headers
  // only set site context header if request not authenticated
  operation.setContext({
    headers: {
      ...(typeof headers === 'object' && headers != null && headers),
      'x-dfh-site-context': isDispensary ? 'dispensary' : 'global',
    },
  })
  return forward(operation)
})

// Client for Commerce API
const commaClient = new ApolloClient({
  link: authMiddleware.concat(siteContextMiddleware.concat(commaLink)),
  cache: new InMemoryCache({
    possibleTypes: commaPossibleTypes.possibleTypes,
    typePolicies: {
      Query: {
        fields: {
          eScripts: relayStylePagination(['orderBy', 'query']),
          customer: {
            merge(existing = {}, incoming) {
              return { ...existing, ...incoming }
            },
          },
        },
      },
      Customer: {
        keyFields: false,
        fields: {
          orders: relayStylePagination(),
          rewardsTransactions: relayStylePagination(['classification', 'dateRange']),
        },
      },
      Filter: {
        keyFields: false,
      },
    },
  }),
  defaultOptions: {
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },

  // Dev tools does not currently support multiple clients. Feature should be available soon,
  // though: https://github.com/apollographql/apollo-client-devtools/issues/822
  connectToDevTools: true,
})

// Magento client is used as the default client
const magentoClient = new ApolloClient({
  link: ApolloLink.from([
    authMiddleware,
    siteContextMiddleware,
    createExcludeLiveRatesLink(process.env.GATSBY_LIVE_RATE_SKIPPING === 'true'),
    magentoLink,
  ]),
  cache: new InMemoryCache({
    typePolicies: {
      Cart: {
        fields: {
          available_payment_methods: {
            merge: false,
          },
        },
      },
      ConfigurableProduct: {
        keyFields: ['sku'],
        // workaround Magento bug not returning fields instead of returning `null`
        fields: {
          content_left_both: (value) => value ?? null,
          content_left_patient: (value) => value ?? null,
          content_left_professional: (value) => value ?? null,
          content_left_public: (value) => value ?? null,
          content_right_both: (value) => value ?? null,
          content_right_patient: (value) => value ?? null,
          content_right_professional: (value) => value ?? null,
          content_right_public: (value) => value ?? null,
          description: (value) => value ?? null,
        },
      },
      // use `email` field for M2 customer type since `id` in response is null
      Customer: {
        keyFields: ['email'],
      },
      // specify key fields for types without `id` field
      CustomerAutoShip: {
        keyFields: ['autoShipId'],
      },
      CustomerOrder: {
        keyFields: (obj) =>
          // use `orderId` when present (Camel), otherwise `id` (Magento)
          typeof obj?.orderId === 'string' && obj.orderId
            ? obj.orderId
            : typeof obj?.id === 'string' && obj.id
              ? obj.id
              : undefined,
      },
      CustomerInvoice: {
        keyFields: ['invoiceId'],
      },
      CustomerRebateRedemptionCashRequest: {
        keyFields: ['requestId'],
      },
      CustomerSettings: {
        keyFields: ['customerId'],
      },
      FacetedResultsAggregation: {
        keyFields: false,
      },
      SelectedCustomizableOption: {
        keyFields: false,
      },
      SimpleProduct: {
        keyFields: ['sku'],
        // workaround Magento bug not returning fields instead of returning `null`
        fields: {
          content_left_both: (value) => value ?? null,
          content_left_patient: (value) => value ?? null,
          content_left_professional: (value) => value ?? null,
          content_left_public: (value) => value ?? null,
          content_right_both: (value) => value ?? null,
          content_right_patient: (value) => value ?? null,
          content_right_professional: (value) => value ?? null,
          content_right_public: (value) => value ?? null,
          description: (value) => value ?? null,
        },
      },
      TokenBaseCard: {
        keyFields: ['hash'],
      },
      Query: {
        fields: {
          getCustomerInvoices: {
            keyArgs: ['customerId'],
            merge(existing, incoming) {
              if (!existing) {
                return incoming
              }
              const existingPageInfo = existing?.meta?.pageInfo
              const incomingPageInfo = incoming?.meta?.pageInfo
              // This logic assumes a "load more" behavior: incoming pages should always append
              // to the existing data. Return existing data if otherwise
              if (
                !existingPageInfo ||
                !incomingPageInfo ||
                incomingPageInfo.currentPage <= existingPageInfo.currentPage
              ) {
                return existing
              }
              return {
                ...incoming,
                invoices: [...(existing.invoices || []), ...(incoming.invoices || [])],
              }
            },
          },
          getCustomerRewardsOrders: {
            keyArgs: ['customerId', 'rewardsSource'],
            merge(existing, incoming) {
              if (!existing) {
                return incoming
              }
              const existingPageInfo = existing?.meta?.pageInfo
              const incomingPageInfo = incoming?.meta?.pageInfo
              // This logic assumes a "load more" behavior: incoming pages should always append
              // to the existing data. Return existing data if otherwise
              if (
                !existingPageInfo ||
                !incomingPageInfo ||
                incomingPageInfo.currentPage <= existingPageInfo.currentPage
              ) {
                return existing
              }
              return {
                ...incoming,
                orders: [...(existing.orders || []), ...(incoming.orders || [])],
              }
            },
          },
          getCustomerAutoShips: {
            keyArgs: ['customerId'],
            merge(existing, incoming, { readField }) {
              if (!existing) {
                return incoming
              }
              const existingPageInfo = existing?.meta?.pageInfo
              const incomingPageInfo = incoming?.meta?.pageInfo
              // This logic assumes a "load more" behavior: incoming pages should always append
              // to the existing data. Return existing data if otherwise
              if (
                !existingPageInfo ||
                !incomingPageInfo ||
                incomingPageInfo.currentPage <= existingPageInfo.currentPage
              ) {
                return existing
              }
              // Append the next page, deduped by autoShipId. Array membership isn't deduped by the
              // normalized cache even though CustomerAutoShip has keyFields ['autoShipId'], so a
              // record that shifts between pages server-side would otherwise appear twice and
              // collide on the React key.
              const merged = [...(existing.autoShips || []), ...(incoming.autoShips || [])]
              const seen = new Set<unknown>()
              const autoShips = merged.filter((autoShipRef) => {
                const id = readField('autoShipId', autoShipRef)
                if (id == null) {
                  return true
                }
                if (seen.has(id)) {
                  return false
                }
                seen.add(id)
                return true
              })
              return {
                ...incoming,
                autoShips,
              }
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
  connectToDevTools: false,
})

const clients: ApolloClients = {
  comma: commaClient,
  magento: magentoClient,
}

const latestResetStoreToken$ = new BehaviorSubject<string | undefined>(undefined)

export const resetTokenStore$ = (token?: string) => {
  return latestResetStoreToken$.asObservable().pipe(
    take(1),
    switchMap((latestToken) => {
      // avoid resetting store when token initially received
      if (latestToken && token !== latestToken) {
        // user signed out or token updated - clear cache and refetch
        const magentoClientReset = from(
          magentoClient.resetStore().catch((err) => {
            logAndCaptureException(err)
          }),
        )

        const commaClientReset = from(
          commaClient.resetStore().catch((err) => {
            logAndCaptureException(err)
          }),
        )

        return forkJoin([magentoClientReset, commaClientReset])
      }
      return of(null)
    }),
    tap(() => {
      latestResetStoreToken$.next(token)
    }),
  )
}

const AuthorizedApolloProvider: React.FC = ({ children }) => {
  const token = useContext(TokenContext)

  useEffect(() => {
    const subscription = resetTokenStore$(token).subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [token])

  // hold subscription for child components and set clients on mount
  useEffect(() => {
    const subscription = latestApolloClients$.subscribe()

    setApolloClients(clients)

    return () => subscription.unsubscribe()
  }, [])

  return (
    <ApolloClientsContext.Provider value={clients}>
      <ApolloProvider client={magentoClient}>{children}</ApolloProvider>
    </ApolloClientsContext.Provider>
  )
}

export default AuthorizedApolloProvider
